import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import { Link } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = import.meta.env.VITE_API_URL;
const IMAGE_URL = import.meta.env.VITE_IMAGE_URL;

interface Carousel {
  productsCarouselId: number;
  image?: string;
  index: number;
}

interface SubCategory {
  subCategoryId: number;
  name: string;
  carousels: Carousel[];
}

interface Category {
  categoryId: number;
  name: string;
  subCategories: SubCategory[];
  carousels: Carousel[];
}

function SortableItem({
  id,
  children,
}: {
  id: number;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Box display="flex" alignItems="center">
        <Box {...listeners} {...attributes} sx={{ cursor: 'grab', mr: 1 }}>
          <DragIndicatorIcon />
        </Box>
        {children}
      </Box>
    </div>
  );
}

export default function CarouselStructure() {
  const [data, setData] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    fetch(`${API_URL}/categories-block`)
      .then((res) => res.json())
      .then((res) => {
        setData(res);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const saveOrder = async (carousels: Carousel[]) => {
    await fetch(`${API_URL}/carousel/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        carousels: carousels.map((c, i) => ({
          productsCarouselId: c.productsCarouselId,
          index: i + 1,
        })),
      }),
    });
  };

  const handleDragEnd = (
    event: any,
    type: 'category' | 'subCategory',
    parentId: number,
    carousels: Carousel[],
  ) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = carousels.findIndex(
      (c) => c.productsCarouselId === active.id,
    );
    const newIndex = carousels.findIndex(
      (c) => c.productsCarouselId === over.id,
    );

    const updated = arrayMove(carousels, oldIndex, newIndex);

    setData((prev) =>
      prev.map((cat) => {
        if (type === 'category' && cat.categoryId === parentId) {
          return { ...cat, carousels: updated };
        }

        if (type === 'subCategory') {
          return {
            ...cat,
            subCategories: cat.subCategories.map((sub) =>
              sub.subCategoryId === parentId
                ? { ...sub, carousels: updated }
                : sub,
            ),
          };
        }

        return cat;
      }),
    );

    saveOrder(updated);
  };

  const renderCarousels = (
    carousels: Carousel[],
    type: 'category' | 'subCategory',
    parentId: number,
  ) => (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={(e) => handleDragEnd(e, type, parentId, carousels)}
    >
      <SortableContext
        items={carousels.map((c) => c.productsCarouselId)}
        strategy={verticalListSortingStrategy}
      >
        <Stack spacing={2}>
          {carousels.map((carousel, idx) => (
            <SortableItem
              key={carousel.productsCarouselId}
              id={carousel.productsCarouselId}
            >
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Typography>Каталог #{idx + 1}</Typography>
                <Typography>Код: {carousel.productsCarouselId}</Typography>

                {carousel.image && (
                  <Box
                    component="img"
                    src={`${IMAGE_URL}/${carousel.image}`}
                    sx={{
                      height: 50,
                      borderRadius: 2,
                      cursor: 'pointer',
                      mx: 2,
                    }}
                    onClick={() => setPreview(`${IMAGE_URL}/${carousel.image}`)}
                  />
                )}

                <Button
                  component={Link}
                  to={`/carousel/${carousel.productsCarouselId}`}
                  startIcon={<EditIcon />}
                >
                  Редагувати
                </Button>
              </Paper>
            </SortableItem>
          ))}
        </Stack>
      </SortableContext>
    </DndContext>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Stack spacing={4} marginTop={4}>
        {data.map((category) => (
          <Box key={category.categoryId}>
            <Typography
              variant="h6"
              mb={2}
              dangerouslySetInnerHTML={{ __html: category.name }}
            />

            {category.subCategories.length > 0
              ? category.subCategories.map((sub) => (
                  <Paper key={sub.subCategoryId} sx={{ p: 3, mb: 3 }}>
                    <Typography mb={2}>{sub.name}</Typography>
                    {renderCarousels(
                      sub.carousels,
                      'subCategory',
                      sub.subCategoryId,
                    )}
                  </Paper>
                ))
              : renderCarousels(
                  category.carousels,
                  'category',
                  category.categoryId,
                )}
          </Box>
        ))}
      </Stack>

      <Dialog
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        maxWidth="md"
      >
        <DialogContent>
          {preview && (
            <Box
              component="img"
              src={preview}
              sx={{ width: '100%', borderRadius: 2 }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
